import { useDispatch } from "react-redux";
import useCountDown from "react-countdown-hook";
import { AppEventsLogger } from "react-native-fbsdk-next";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useState, useEffect, memo, useCallback, useRef } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  BackHandler,
} from "react-native";

// Custom Imports
import color from "@constants/color";
import { scale } from "@utils/scale";
import t from "@constants/localization";
import analytics from "@services/analytics";
import { fontFamily } from "@utils/styles";
import CallIcon from "@assets/svg/ClTasks/CallIcon";
import { IS_DEVICE_VERIFIED } from "@constants/strings";
import { loginRefresh, retryDevice } from "@services/api";
import CitymallLogo from "@assets/svg/TwoFactor/CitymallLogo";
import { actions as miscActions } from "@reducers/miscReducer";
import { actions as configActions } from "@reducers/configReducer";
import {
  SIGNUP_FLOW_EVENTS,
  SIGNUP_FLOW_SOURCES,
} from "@constants/analyticsEventsCT";
import TimeUpdateIcon from "@assets/svg/TwoFactor/TimeUpdateIcon";
import TwoAuthWarningIcon from "../../assets/svg/TwoFactor/WarningIcon";

const PATH = "screens/Auth/TwoFactorAuth";
const COMPLETE_SIGNIN = "fb_mobile_activate_app";

const DeniedAcessComponent = memo(() => {
  return (
    <>
      <TwoAuthWarningIcon style={styles.rejectedIcon} />
      <Text
        style={[
          styles.verifyItsYou,
          {
            marginTop: 20,
          },
        ]}
      >
        {t(`${PATH}/accessDenied`)}
      </Text>
      <Text style={styles.loginDueToAnotherDevice}>
        {t(`${PATH}/youAreNotAllowed`)}
      </Text>
    </>
  );
});

const CallingLimitExceedComponent = memo(({ time = 1 }) => {
  const exitApp = () => {
    BackHandler.exitApp();
  };
  return (
    <>
      <View style={styles.rejectedIcon}>
        <TimeUpdateIcon />
        <Text style={styles.hourText}>{`${time}h`}</Text>
      </View>
      <Text
        style={[
          styles.verifyItsYou,
          {
            marginTop: 20,
          },
        ]}
      >
        {t(`${PATH}/sorry`)}
      </Text>
      <Text style={styles.loginDueToAnotherDevice}>
        {t(
          `${PATH}/${
            time === 1 ? "tryAgainAfterOneHour" : "tryAgainAfter24hour"
          }`,
        )}
      </Text>
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={exitApp}
        style={styles.okayButtonContainer}
      >
        <Text style={styles.okayText}>{t(`${PATH}/okay`)}</Text>
      </TouchableOpacity>
    </>
  );
});

const TwoFactorAuth = memo(({ navigation, route }) => {
  const dispatch = useDispatch();
  const [countdown, { start, pause }] = useCountDown(60 * 1000, 1000);
  const [isCountdownRunning, setIsCountdownRunning] = useState(true);
  const [shouldShowResendOption, setShouldShowResendOption] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(
    IS_DEVICE_VERIFIED.PENDING,
  );
  const {
    params: {
      newTcUser = false,
      phone_number = "",
      callingLimitExceed = null,
    } = {},
  } = route;
  const [ivrSessionId, setIvrSessionId] = useState(route?.params?.ivrSessionId);
  const [limitExceed, setLimitExceed] = useState(callingLimitExceed);
  const timer = useRef(null);

  useEffect(() => {
    if (callingLimitExceed === null) {
      timer.current && clearInterval(timer.current);
      timer.current = setInterval(ivrSessionStatus, 3000);
    }
    return () => clearInterval(timer.current);
  }, [ivrSessionId]);

  useEffect(() => {
    start();
  }, [start]);

  const resetCountdown = useCallback(() => {
    start();
  }, [start]);

  useEffect(() => {
    if (countdown === 0 && shouldShowResendOption) {
      setIsCountdownRunning(true);
    } else if (countdown === 0) {
      setShouldShowResendOption(true);
      setIsCountdownRunning(false);
    } else {
      setIsCountdownRunning(true);
      setShouldShowResendOption(false);
    }
  }, [countdown]);

  const clearState = () => {
    clearInterval(timer.current);
    pause();
  };

  const ivrSessionStatus = useCallback(async () => {
    try {
      const { data } = await loginRefresh({
        ivrSessionId,
        phone_number,
        newTcUser,
      });
      const { token = "", is_device_verified = "" } = data;
      if (is_device_verified === IS_DEVICE_VERIFIED.ACCEPTED) {
        clearState();
        await Promise.all([
          AsyncStorage.setItem("user_token", token), // save in the async storage before me call
          AsyncStorage.setItem("showCLToCLWelcome", "true"),
        ]);
        dispatch(configActions.syncUser()); // --> call sync
        analytics.trackEvent(SIGNUP_FLOW_EVENTS.NUMBER_VERIFIED, {
          number_source: SIGNUP_FLOW_SOURCES.STANDARD,
        });
        analytics.trackEvent(SIGNUP_FLOW_EVENTS.SUCCESSFULL_LOGIN);
        AppEventsLogger.logEvent(COMPLETE_SIGNIN, {
          currency: "INR",
        });
      } else if (is_device_verified === IS_DEVICE_VERIFIED.REJECTED) {
        setCurrentStatus(is_device_verified);
        clearState();
      }
    } catch (error) {
      console.log("ivrSessionStatus_err", error?.response?.data);
    }
  }, [currentStatus, limitExceed, ivrSessionId]);

  const deviceVerificationRetry = useCallback(async () => {
    try {
      resetCountdown();
      const { data } = await retryDevice({
        phone_number,
        ivrSessionId,
      });
      if (data?.ivrSessionId) {
        setIvrSessionId(data.ivrSessionId);
      } else if (data?.callingLimitExceed) {
        setLimitExceed(data.callingLimitExceed);
        clearState();
      } else if (data?.message == "Device is already verified") {
        ivrSessionStatus();
      } else {
        setLimitExceed("wait_for_1_hour");
        clearState();
      }
    } catch (error) {
      setLimitExceed("wait_for_1_hour");
      clearState();
      console.log("deviceVerificationRetry api error", error?.response?.data);
    }
  }, [ivrSessionId, limitExceed]);

  return (
    <ScrollView
      contentContainerStyle={styles.contentContainerStyle}
      style={styles.container}
    >
      <CitymallLogo style={styles.logoStyle} />
      {limitExceed && limitExceed !== null ? (
        <CallingLimitExceedComponent
          time={limitExceed === "wait_for_24_hour" ? 24 : 1}
        />
      ) : currentStatus === IS_DEVICE_VERIFIED.REJECTED ? (
        <DeniedAcessComponent />
      ) : (
        <>
          <Text style={styles.verifyItsYou}>{t(`${PATH}/verifyItsYou`)}</Text>
          <Text style={styles.loginDueToAnotherDevice}>
            {t(`${PATH}/loginDueToAnotherDevice`)}
          </Text>
          <View style={styles.callIconContainer}>
            <CallIcon width={24} height={24} />
            <Text style={styles.someTimeLater}>
              {t(`${PATH}/someTimeLater`)}
            </Text>
          </View>
          <View style={styles.timerContainer}>
            {isCountdownRunning && (
              <Text style={styles.timerStyle}>
                00:
                {isCountdownRunning
                  ? `${countdown / 1000}`.length === 1
                    ? `0${countdown / 1000}`
                    : countdown / 1000
                  : "00"}
              </Text>
            )}
            <Text
              disabled={isCountdownRunning}
              onPress={deviceVerificationRetry}
              style={[
                styles.requestAgain,
                {
                  color: isCountdownRunning
                    ? color.lightMediumBlack
                    : color.primary,
                  opacity: isCountdownRunning ? 0.3 : 1,
                },
              ]}
            >
              {t(`${PATH}/requestAgain`)}
            </Text>
          </View>
        </>
      )}
    </ScrollView>
  );
});

export default TwoFactorAuth;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainerStyle: {
    backgroundColor: color.white,
    paddingHorizontal: 16,
    flexGrow: 1,
  },
  logoStyle: {
    marginTop: 30,
  },
  verifyItsYou: {
    fontSize: 20,
    fontFamily: fontFamily.bold,
    color: color.mediumBlack,
    marginTop: 147,
    alignSelf: "center",
  },
  loginDueToAnotherDevice: {
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: color.mediumBlack,
    marginTop: 4,
    alignSelf: "center",
    textAlign: "center",
  },
  callIconContainer: {
    flexDirection: "row",
    alignSelf: "center",
    marginTop: 42,
    alignItems: "center",
  },
  someTimeLater: {
    fontSize: 16,
    fontFamily: fontFamily.bold,
    color: color.mediumBlack,
    marginStart: 8,
  },
  timerContainer: {
    marginTop: 12,
    flexDirection: "row",
    alignSelf: "center",
    alignItems: "center",
  },
  requestAgain: {
    fontSize: 14,
    fontFamily: fontFamily.semiBold,
    color: color.lightMediumBlack,
    marginStart: 8,
    textDecorationLine: "underline",
  },
  timerStyle: {
    fontSize: 14,
    fontFamily: fontFamily.semiBold,
    color: color.mediumBlack,
  },
  okayButtonContainer: {
    height: 40,
    borderRadius: 8,
    backgroundColor: color.primary,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
    alignSelf: "center",
    width: scale(120),
  },
  okayText: {
    fontSize: 16,
    fontFamily: fontFamily.semiBold,
    color: color.white,
    marginBottom: 4,
    lineHeight: 28,
  },
  rejectedIcon: {
    marginTop: scale(145),
    alignSelf: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  hourText: {
    fontSize: 14,
    fontFamily: fontFamily.bold,
    color: color.mediumBlack,
    lineHeight: 24,
    position: "absolute",
    alignSelf: "center",
  },
});
